import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

type StoryVariant = 'logo' | 'lockup';
type AssetTone = 'white' | 'black';

const STORY_WIDTH = 1080;
const STORY_HEIGHT = 1920;
const STICKER_WIDTH = 1080;
const STICKER_HEIGHT = 620;
const WHEEL_SIZE = 900;
const DURATION_MS = 6000;

const drawBackground = (ctx: CanvasRenderingContext2D, t: number) => {
  const base = ctx.createLinearGradient(0, 0, STORY_WIDTH, STORY_HEIGHT);
  base.addColorStop(0, '#02040a');
  base.addColorStop(0.46, '#080b12');
  base.addColorStop(1, '#010206');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT);

  const glowA = ctx.createRadialGradient(360 + Math.sin(t * 0.55) * 40, 710, 0, 360, 710, 760);
  glowA.addColorStop(0, 'rgba(118, 131, 151, 0.22)');
  glowA.addColorStop(0.45, 'rgba(44, 55, 74, 0.12)');
  glowA.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = glowA;
  ctx.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT);

  const glowB = ctx.createRadialGradient(900, 1220 + Math.cos(t * 0.45) * 35, 0, 900, 1220, 780);
  glowB.addColorStop(0, 'rgba(255, 255, 255, 0.10)');
  glowB.addColorStop(0.4, 'rgba(72, 84, 104, 0.10)');
  glowB.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = glowB;
  ctx.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT);

  ctx.save();
  ctx.globalAlpha = 0.1;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1;
  for (let y = 0; y < STORY_HEIGHT; y += 18) {
    ctx.beginPath();
    ctx.moveTo(0, y + ((t * 12) % 18));
    ctx.lineTo(STORY_WIDTH, y + ((t * 12) % 18));
    ctx.stroke();
  }
  ctx.restore();
};

const drawBrandText = (ctx: CanvasRenderingContext2D, x: number, y: number, tone: AssetTone = 'white') => {
  const isWhite = tone === 'white';
  ctx.save();
  ctx.textBaseline = 'top';
  ctx.letterSpacing = '-5px';
  ctx.shadowColor = isWhite ? 'rgba(255,255,255,0.34)' : 'rgba(0,0,0,0.34)';
  ctx.shadowBlur = 30;
  ctx.font = "900 128px 'Geist Pixel Grid', 'Geist Mono', monospace";
  ctx.fillStyle = isWhite ? '#f8fafc' : '#02040a';
  ctx.fillText('Musashi', x, y);
  ctx.fillStyle = isWhite ? 'rgba(248,250,252,0.72)' : 'rgba(2,4,10,0.72)';
  ctx.fillText('Studio', x, y + 120);

  ctx.shadowBlur = 0;
  ctx.font = "600 25px 'Geist Mono', monospace";
  ctx.letterSpacing = '6px';
  ctx.fillStyle = isWhite ? 'rgba(226,232,240,0.64)' : 'rgba(2,4,10,0.58)';
  ctx.fillText('DIGITAL PRODUCT STUDIO', x + 4, y + 278);
  ctx.restore();
};

const drawCheckerboard = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
  const size = 48;
  for (let y = 0; y < height; y += size) {
    for (let x = 0; x < width; x += size) {
      ctx.fillStyle = (x / size + y / size) % 2 === 0 ? '#d9dee7' : '#aeb7c6';
      ctx.fillRect(x, y, size, size);
    }
  }
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const downloadCanvasPng = (canvas: HTMLCanvasElement, filename: string) => {
  canvas.toBlob((blob) => {
    if (blob) downloadBlob(blob, filename);
  }, 'image/png');
};

const StoryAssets: React.FC = () => {
  const outputRef = useRef<HTMLCanvasElement>(null);
  const stickerRef = useRef<HTMLCanvasElement>(null);
  const wheelRef = useRef<HTMLCanvasElement>(null);
  const variantRef = useRef<StoryVariant>('lockup');
  const toneRef = useRef<AssetTone>('white');
  const [variant, setVariant] = useState<StoryVariant>('lockup');
  const [tone, setTone] = useState<AssetTone>('white');
  const [ready, setReady] = useState(false);
  const [recording, setRecording] = useState(false);

  useEffect(() => {
    variantRef.current = variant;
  }, [variant]);

  useEffect(() => {
    toneRef.current = tone;
  }, [tone]);

  useEffect(() => {
    const output = outputRef.current;
    const sticker = stickerRef.current;
    const wheelCanvas = wheelRef.current;
    if (!output || !sticker || !wheelCanvas) return;

    const ctx = output.getContext('2d');
    const stickerCtx = sticker.getContext('2d', { alpha: true });
    const webglContext = wheelCanvas.getContext('webgl2', { alpha: true, antialias: false, preserveDrawingBuffer: true });
    if (!ctx || !stickerCtx || !webglContext) return;

    output.width = STORY_WIDTH;
    output.height = STORY_HEIGHT;
    sticker.width = STICKER_WIDTH;
    sticker.height = STICKER_HEIGHT;
    wheelCanvas.width = WHEEL_SIZE;
    wheelCanvas.height = WHEEL_SIZE;

    const renderer = new THREE.WebGLRenderer({
      canvas: wheelCanvas,
      context: webglContext,
      alpha: true,
      antialias: false,
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(1);
    renderer.setSize(WHEEL_SIZE, WHEEL_SIZE, false);
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1.65, 1.65, 1.65, -1.65, 0.1, 100);
    camera.position.set(0, 0, 5);
    camera.lookAt(0, 0, 0);

    const group = new THREE.Group();
    scene.add(group);
    scene.add(new THREE.AmbientLight(0xffffff, 1));

    let model: THREE.Object3D | null = null;
    let frame = 0;
    let disposed = false;
    const modelMaterials: THREE.MeshBasicMaterial[] = [];

    const material = new THREE.MeshBasicMaterial({ color: new THREE.Color('#f8fafc') });
    new GLTFLoader().load('/3dWheelModel.glb', (gltf) => {
      if (disposed) return;
      model = gltf.scene;
      model.rotation.set(Math.PI / 2, 0, 0);
      model.traverse((obj) => {
        const mesh = obj as THREE.Mesh;
        if (mesh.isMesh) {
          mesh.castShadow = false;
          mesh.receiveShadow = false;
          const meshMaterial = material.clone();
          modelMaterials.push(meshMaterial);
          mesh.material = meshMaterial;
        }
      });
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      model.position.sub(center);
      model.scale.setScalar(2.65 / Math.max(size.x, size.y, size.z, 0.001));
      group.add(model);
      setReady(true);
    });

    const drawSticker = (targetCtx: CanvasRenderingContext2D, current: StoryVariant, currentTone: AssetTone) => {
      const isWhite = currentTone === 'white';
      targetCtx.clearRect(0, 0, STICKER_WIDTH, STICKER_HEIGHT);

      if (current === 'logo') {
        targetCtx.save();
        targetCtx.shadowColor = isWhite ? 'rgba(255,255,255,0.26)' : 'rgba(0,0,0,0.26)';
        targetCtx.shadowBlur = 28;
        targetCtx.drawImage(wheelCanvas, 170, 20, 740, 740);
        targetCtx.restore();
      } else {
        targetCtx.save();
        targetCtx.shadowColor = isWhite ? 'rgba(255,255,255,0.24)' : 'rgba(0,0,0,0.24)';
        targetCtx.shadowBlur = 24;
        targetCtx.drawImage(wheelCanvas, 65, 85, 450, 450);
        targetCtx.restore();
        drawBrandText(targetCtx, 555, 145, currentTone);
      }
    };

    const draw = (now: number) => {
      const t = now / 1000;
      const current = variantRef.current;
      const currentTone = toneRef.current;
      const isWhite = currentTone === 'white';
      const color = new THREE.Color(isWhite ? '#f8fafc' : '#02040a');
      modelMaterials.forEach((meshMaterial) => meshMaterial.color.copy(color));

      group.rotation.set(0, 0, -t * 0.85);
      renderer.clear();
      renderer.render(scene, camera);
      drawSticker(stickerCtx, current, currentTone);

      drawCheckerboard(ctx, STORY_WIDTH, STORY_HEIGHT);
      ctx.save();
      ctx.fillStyle = isWhite ? 'rgba(2,4,10,0.78)' : 'rgba(255,255,255,0.76)';
      ctx.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT);
      ctx.restore();
      ctx.drawImage(sticker, 0, 650, STICKER_WIDTH, STICKER_HEIGHT);

      frame = window.requestAnimationFrame(draw);
    };

    frame = window.requestAnimationFrame(draw);

    return () => {
      disposed = true;
      window.cancelAnimationFrame(frame);
      material.dispose();
      if (model) {
        model.traverse((obj) => {
          const mesh = obj as THREE.Mesh;
          if (mesh.isMesh) {
            mesh.geometry?.dispose();
            const mat = mesh.material;
            if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
            else mat?.dispose();
          }
        });
      }
      renderer.dispose();
    };
  }, []);

  const exportTransparentPng = (nextVariant: StoryVariant, nextTone: AssetTone) => {
    const sticker = stickerRef.current;
    if (!sticker || !ready) return;
    setVariant(nextVariant);
    setTone(nextTone);
    variantRef.current = nextVariant;
    toneRef.current = nextTone;
    window.setTimeout(() => downloadCanvasPng(sticker, `musashi-${nextTone}-${nextVariant}-transparent.png`), 80);
  };

  const record = async (nextVariant: StoryVariant, nextTone: AssetTone) => {
    const sticker = stickerRef.current;
    if (!sticker || recording) return;
    setVariant(nextVariant);
    setTone(nextTone);
    variantRef.current = nextVariant;
    toneRef.current = nextTone;
    setRecording(true);

    await new Promise((resolve) => window.setTimeout(resolve, 120));
    const stream = sticker.captureStream(30);
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : 'video/webm';
    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 10_000_000 });
    const chunks: BlobPart[] = [];

    recorder.ondataavailable = (event) => {
      if (event.data.size) chunks.push(event.data);
    };
    recorder.onstop = () => {
      downloadBlob(new Blob(chunks, { type: 'video/webm' }), `musashi-${nextTone}-${nextVariant}-transparent-animated.webm`);
      setRecording(false);
    };

    recorder.start();
    window.setTimeout(() => recorder.stop(), DURATION_MS);
  };

  return (
    <main className="min-h-screen bg-[#02040a] text-white px-5 py-6">
      <div className="mx-auto max-w-6xl grid gap-6 lg:grid-cols-[auto_1fr] items-start">
        <canvas
          ref={outputRef}
          className="w-[337px] max-w-full rounded-[28px] border border-white/15 shadow-2xl"
          style={{ aspectRatio: '9 / 16' }}
        />
        <canvas ref={stickerRef} className="hidden" />
        <canvas ref={wheelRef} className="hidden" />

        <section className="max-w-xl pt-2">
          <p className="text-xs uppercase tracking-[0.28em] text-white/45">Instagram Story Export</p>
          <h1 className="mt-3 font-pixel text-4xl sm:text-5xl font-black leading-none">Musashi motion assets</h1>
          <p className="mt-5 text-white/65 leading-relaxed">
            Transparent animated sticker exports: only the spinning wheel and Musashi Studio text, no rectangle/background.
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setVariant('lockup')}
              className={`rounded-full border px-5 py-3 text-sm font-semibold ${variant === 'lockup' ? 'border-white bg-white text-black' : 'border-white/20 text-white/75'}`}
            >
              Logo + text
            </button>
            <button
              type="button"
              onClick={() => setVariant('logo')}
              className={`rounded-full border px-5 py-3 text-sm font-semibold ${variant === 'logo' ? 'border-white bg-white text-black' : 'border-white/20 text-white/75'}`}
            >
              Wheel only
            </button>
            <button
              type="button"
              onClick={() => setTone('white')}
              className={`rounded-full border px-5 py-3 text-sm font-semibold ${tone === 'white' ? 'border-white bg-white text-black' : 'border-white/20 text-white/75'}`}
            >
              White version
            </button>
            <button
              type="button"
              onClick={() => setTone('black')}
              className={`rounded-full border px-5 py-3 text-sm font-semibold ${tone === 'black' ? 'border-white bg-white text-black' : 'border-white/20 text-white/75'}`}
            >
              Black version
            </button>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={!ready || recording}
              onClick={() => record('lockup', 'white')}
              className="rounded-full bg-emerald-300 px-5 py-3 text-sm font-bold text-black disabled:opacity-40"
            >
              {recording ? 'Recording…' : 'Download WHITE animated sticker'}
            </button>
            <button
              type="button"
              disabled={!ready || recording}
              onClick={() => record('lockup', 'black')}
              className="rounded-full bg-emerald-300 px-5 py-3 text-sm font-bold text-black disabled:opacity-40"
            >
              {recording ? 'Recording…' : 'Download BLACK animated sticker'}
            </button>
          </div>

          <div className="mt-3 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={!ready}
              onClick={() => exportTransparentPng('lockup', 'white')}
              className="rounded-full border border-white/20 px-5 py-3 text-sm font-semibold text-white/75 disabled:opacity-40"
            >
              White PNG preview
            </button>
            <button
              type="button"
              disabled={!ready}
              onClick={() => exportTransparentPng('lockup', 'black')}
              className="rounded-full border border-white/20 px-5 py-3 text-sm font-semibold text-white/75 disabled:opacity-40"
            >
              Black PNG preview
            </button>
          </div>

          <p className="mt-5 text-sm text-white/45">
            The WebM files are transparent stickers: no black background, just the animated wheel and glowing text.
          </p>
        </section>
      </div>
    </main>
  );
};

export default StoryAssets;
